/**
 * TanStack Query hooks + keys (ARCHITECTURE §4.2, api.md v2).
 *
 * Server state lives here, never in zustand. Artifact queries are
 * ETag-backed via the browser HTTP cache with `staleTime: Infinity` —
 * immutable-until-invalidated; the ONLY invalidation trigger is a
 * `job.done` frame (call `invalidateAfterJobDone`).
 *
 * v2 key hierarchy (BUILDPLAN2 WS-G): everything product-scoped lives under
 * `['product', pid, …]`, everything repo-scoped under `['repo', rid, …]` —
 * one prefix invalidation reaches a whole scope. Repo ULIDs equal the v1
 * project ids (migration preserves them byte-identical).
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  uploadRepoZip,
  type UploadProgress,
} from './client';
import { rememberAuth } from '../features/auth/rememberedAuth';
import type {
  AuthSessionProbe,
  ChatSessionCreated,
  ChatSessionDetail,
  ChatSessionList,
  CreateChatSessionRequest,
  CreateJobRequest,
  CreateProductRequest,
  ChangeSourceRequest,
  CreateRepoRequest,
  HealthResponse,
  Job,
  JobCreated,
  LocalPickRequest,
  LocalPickResponse,
  LoginRequest,
  MeResponse,
  PatchMeRequest,
  PatchProductRequest,
  PatchRepoRequest,
  ProductDetail,
  ProductList,
  ProvenanceResponse,
  ReaderNote,
  ReaderNotesResponse,
  RefreshPreview,
  RepoDetail,
  RepoStatus,
  SessionInfo,
  SignupRequest,
  Ulid,
} from './types';

export type ArtifactKind = 'graph' | 'narration' | 'flows';

// ---------------------------------------------------------------------------
// Query keys — the single registry (features import these, never inline keys)
// ---------------------------------------------------------------------------

export const queryKeys = {
  health: ['health'] as const,
  /** GET /api/auth/session — the SPA probe (never 401s). */
  authSession: ['auth', 'session'] as const,
  /** GET /api/me — account + BYOK. */
  me: ['me'] as const,

  // product scope -----------------------------------------------------------
  products: ['products'] as const,
  product: (pid: Ulid) => ['product', pid] as const,
  /** GET /api/products/{pid}/chat/sessions */
  sessions: (pid: Ulid) => ['product', pid, 'sessions'] as const,
  /** GET /api/products/{pid}/reader_notes (DECISIONS #79) */
  readerNotes: (pid: Ulid) => ['product', pid, 'reader-notes'] as const,

  // repo scope --------------------------------------------------------------
  repo: (rid: Ulid) => ['repo', rid] as const,
  repoStatus: (rid: Ulid) => ['repo', rid, 'status'] as const,
  refreshPreview: (rid: Ulid) => ['repo', rid, 'refresh-preview'] as const,
  provenance: (rid: Ulid) => ['repo', rid, 'provenance'] as const,
  artifact: (rid: Ulid, kind: ArtifactKind) => ['repo', rid, 'artifact', kind] as const,
  /**
   * GET /api/repos/{rid}/infra/live — the LIVEINFRA overlay doc (presentation-
   * layer polling, never SSE). Hooks live in `features/liveinfra/api.ts`
   * (their wire types await the api/types.ts mirror from the server
   * workstream); the key lives here so repo-prefix invalidation reaches it.
   */
  infraLive: (rid: Ulid) => ['repo', rid, 'infra', 'live'] as const,
  /** GET /api/repos/{rid}/infra/map — the runtime map (DECISIONS #86). */
  infraMap: (rid: Ulid) => ['repo', rid, 'infra', 'map'] as const,

  // unscoped ----------------------------------------------------------------
  job: (id: Ulid) => ['jobs', id] as const,
  session: (sessionId: Ulid) => ['session', sessionId] as const,
};

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        // A read is fresh for half a minute: every observer that mounts
        // inside that window (a product detail is read by sixteen call
        // sites, one per switcher row) shares the one fetch instead of
        // refetching on mount. Writes invalidate what they change, so
        // nothing here waits on the clock; artifacts keep their own
        // Infinity below.
        staleTime: 30_000,
      },
    },
  });
}

// ---------------------------------------------------------------------------
// Auth (api.md §3)
// ---------------------------------------------------------------------------

/**
 * The SPA auth probe. `GET /api/auth/session` deliberately never 401s, so
 * this query never trips the global auth-required redirect — the route guard
 * branches on `data.authenticated`.
 */
export function useAuthSession(): UseQueryResult<AuthSessionProbe> {
  return useQuery({
    queryKey: queryKeys.authSession,
    queryFn: ({ signal }) => apiGet<AuthSessionProbe>('/auth/session', signal),
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => apiPost<SessionInfo>('/auth/login', body),
    onSuccess: (info) => {
      rememberAuth('in');
      qc.setQueryData<AuthSessionProbe>(queryKeys.authSession, {
        authenticated: true,
        user: info.user,
      });
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignupRequest) => apiPost<SessionInfo>('/auth/signup', body),
    onSuccess: (info) => {
      // A fresh account is signed in, exactly like a login: the next reload
      // renders the protected tree at once (RequireAuth).
      rememberAuth('in');
      qc.setQueryData<AuthSessionProbe>(queryKeys.authSession, {
        authenticated: true,
        user: info.user,
      });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<void>('/auth/logout'),
    onSuccess: () => {
      rememberAuth('out');
      // Everything server-side is per-user now — drop the whole cache.
      qc.setQueryData<AuthSessionProbe>(queryKeys.authSession, { authenticated: false });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    },
  });
}

// ---------------------------------------------------------------------------
// Products (api.md §4)
// ---------------------------------------------------------------------------

export function useProducts(): UseQueryResult<ProductList> {
  return useQuery({
    queryKey: queryKeys.products,
    queryFn: ({ signal }) => apiGet<ProductList>('/products', signal),
  });
}

export function useProduct(pid: Ulid | undefined): UseQueryResult<ProductDetail> {
  return useQuery({
    queryKey: queryKeys.product(pid ?? ''),
    queryFn: ({ signal }) => apiGet<ProductDetail>(`/products/${pid}`, signal),
    enabled: pid !== undefined && pid.length > 0,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductRequest) => apiPost<ProductDetail>('/products', body),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.product(detail.id), detail);
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

export function usePatchProduct(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchProductRequest) => apiPatch<ProductDetail>(`/products/${pid}`, body),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.product(pid), detail);
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pid: Ulid) => apiDelete(`/products/${pid}`),
    onSuccess: (_data, pid) => {
      qc.removeQueries({ queryKey: queryKeys.product(pid) });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

/**
 * Per-product Anthropic key override (DECISIONS #53b). Stored probe-free;
 * 204, never echoed — the fresh `api_key_present`/`api_key_hint` come from
 * refetching the product shapes.
 */
export function usePutProductApiKey(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) =>
      apiPut<void>(`/products/${pid}/api_key`, { api_key: apiKey }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.product(pid) });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

/** Remove the override — the product falls back to the account key. */
export function useDeleteProductApiKey(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/products/${pid}/api_key`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.product(pid) });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

// ---------------------------------------------------------------------------
// Reader notes (api.md §4, DECISIONS #79) — what the guide remembers about
// the reader, in their own words. The agent writes through its own tool; this
// surface is the transparency half: read, add, delete.
// ---------------------------------------------------------------------------

export function useReaderNotes(pid: Ulid): UseQueryResult<ReaderNotesResponse> {
  return useQuery({
    queryKey: queryKeys.readerNotes(pid),
    queryFn: ({ signal }) =>
      apiGet<ReaderNotesResponse>(`/products/${pid}/reader_notes`, signal),
    enabled: pid.length > 0,
  });
}

export function useCreateReaderNote(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      apiPost<ReaderNote>(`/products/${pid}/reader_notes`, { text }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.readerNotes(pid) });
    },
  });
}

export function useDeleteReaderNote(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) =>
      apiDelete(`/products/${pid}/reader_notes/${noteId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.readerNotes(pid) });
    },
  });
}

// ---------------------------------------------------------------------------
// Repos (api.md §5)
// ---------------------------------------------------------------------------

export function useRepo(rid: Ulid | undefined): UseQueryResult<RepoDetail> {
  return useQuery({
    queryKey: queryKeys.repo(rid ?? ''),
    queryFn: ({ signal }) => apiGet<RepoDetail>(`/repos/${rid}`, signal),
    enabled: rid !== undefined && rid.length > 0,
  });
}

/** Polls lightly so the staleness banner stays honest without a refresh. */
export function useRepoStatus(
  rid: Ulid | undefined,
  opts: { refetchIntervalMs?: number | false } = {},
): UseQueryResult<RepoStatus> {
  return useQuery({
    queryKey: queryKeys.repoStatus(rid ?? ''),
    queryFn: ({ signal }) => apiGet<RepoStatus>(`/repos/${rid}/status`, signal),
    enabled: rid !== undefined && rid.length > 0,
    refetchInterval: opts.refetchIntervalMs ?? 60_000,
  });
}

export function useCreateRepo(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRepoRequest) =>
      apiPost<RepoDetail>(`/products/${pid}/repos`, body),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.repo(detail.id), detail);
      void qc.invalidateQueries({ queryKey: queryKeys.product(pid) });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

export function useUploadRepo(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      name: string;
      file: File | Blob;
      onProgress?: (p: UploadProgress) => void;
    }) => uploadRepoZip(pid, vars.name, vars.file, vars.onProgress),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.repo(detail.id), detail);
      void qc.invalidateQueries({ queryKey: queryKeys.product(pid) });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

export function usePatchRepo(rid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchRepoRequest) => apiPatch<RepoDetail>(`/repos/${rid}`, body),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.repo(rid), detail);
      void qc.invalidateQueries({ queryKey: queryKeys.product(detail.product_id) });
    },
  });
}

export function useDeleteRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rid: Ulid) => apiDelete(`/repos/${rid}`),
    onSuccess: (_data, rid) => {
      qc.removeQueries({ queryKey: queryKeys.repo(rid) });
      void qc.invalidateQueries({ queryKey: ['product'] });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

/**
 * PUT /api/repos/{rid}/source — move a codebase to a git address (2026-09-10).
 * The response is the repo as it now is; the status, provenance and product
 * views are refetched because the source facts changed under them.
 */
export function useChangeSource(rid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangeSourceRequest) => apiPut<RepoDetail>(`/repos/${rid}/source`, body),
    onSuccess: (detail) => {
      qc.setQueryData(queryKeys.repo(rid), detail);
      void qc.invalidateQueries({ queryKey: queryKeys.repoStatus(rid) });
      void qc.invalidateQueries({ queryKey: queryKeys.provenance(rid) });
      void qc.invalidateQueries({ queryKey: ['product'] });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

/** GET /api/repos/{rid}/provenance — sync facts + the static access profile. */
export function useProvenance(rid: Ulid | undefined): UseQueryResult<ProvenanceResponse> {
  return useQuery({
    queryKey: queryKeys.provenance(rid ?? ''),
    queryFn: ({ signal }) => apiGet<ProvenanceResponse>(`/repos/${rid}/provenance`, signal),
    enabled: rid !== undefined && rid.length > 0,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Refresh preview / jobs (api.md §6–7)
// ---------------------------------------------------------------------------

/**
 * `GET /api/repos/{rid}/refresh/preview`. Two consumers, one cache entry:
 * - the cost-preview dialog wants fresh numbers on open (`fresh: true`,
 *   default — it runs git fetch);
 * - the refresh chip and tracker read whatever the dialog fetched (the
 *   inline banner that once fetched lazily on mount is gone, 2026-09-05).
 */
export function useRefreshPreview(
  rid: Ulid,
  enabled: boolean,
  opts: { fresh?: boolean } = {},
): UseQueryResult<RefreshPreview> {
  const fresh = opts.fresh ?? true;
  return useQuery({
    queryKey: queryKeys.refreshPreview(rid),
    queryFn: ({ signal }) => apiGet<RefreshPreview>(`/repos/${rid}/refresh/preview`, signal),
    enabled,
    staleTime: fresh ? 0 : 5 * 60_000,
    ...(fresh ? { refetchOnMount: 'always' as const } : {}),
    gcTime: 5 * 60_000,
  });
}

export function useCreateJob(rid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateJobRequest) => apiPost<JobCreated>(`/repos/${rid}/jobs`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.repoStatus(rid) });
      void qc.invalidateQueries({ queryKey: queryKeys.products });
    },
  });
}

export function useCancelJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: Ulid) => apiPost<Job>(`/jobs/${jobId}/cancel`),
    onSuccess: (job) => {
      qc.setQueryData(queryKeys.job(job.id), job);
    },
  });
}

// ---------------------------------------------------------------------------
// Artifacts — immutable-until-invalidated (api.md §8)
// ---------------------------------------------------------------------------

/**
 * The artifact JSON is engine-owned (graph may be v1 or v2) — typed as the
 * caller declares. Map adapters pass their parsed-artifact types and do
 * indexing in `select` so the parse work is shared by reference.
 */
export function useArtifact<T = unknown, TSelected = T>(
  rid: Ulid | undefined,
  kind: ArtifactKind,
  select?: (data: T) => TSelected,
): UseQueryResult<TSelected> {
  return useQuery({
    queryKey: queryKeys.artifact(rid ?? '', kind),
    queryFn: ({ signal }) => apiGet<T>(`/repos/${rid}/artifacts/${kind}`, signal),
    enabled: rid !== undefined && rid.length > 0,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    ...(select ? { select } : {}),
  });
}

/**
 * The one sanctioned invalidation for artifact queries: call when a
 * `job.done` frame arrives. Prefix-invalidates the whole repo scope
 * (artifacts, status, provenance, refresh preview) plus the product scope
 * and the products list.
 */
export function invalidateAfterJobDone(qc: QueryClient, rid: Ulid): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.repo(rid) }),
    qc.invalidateQueries({ queryKey: ['product'] }),
    qc.invalidateQueries({ queryKey: queryKeys.products }),
  ]);
}

/**
 * Live fill during a streaming job (CANVAS2 §3.4): `job.node` / `job.flow`
 * frames may trigger an artifact refetch, but at most once per `intervalMs`
 * (narration.json is one file; per-frame refetch would be wasteful).
 * Trailing-edge debounce: a burst of notifies inside the window collapses to
 * one invalidation at the window's edge, so nothing streamed is ever missed.
 * `flush` fires immediately (phase boundaries); `dispose` cancels the timer.
 */
export function createDebouncedArtifactInvalidator(
  qc: QueryClient,
  rid: Ulid,
  kind: ArtifactKind,
  intervalMs = 5000,
): { notify: () => void; flush: () => void; dispose: () => void } {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const fire = () => {
    last = Date.now();
    void qc.invalidateQueries({ queryKey: queryKeys.artifact(rid, kind) });
  };
  return {
    notify() {
      if (timer !== null) return; // a refetch is already scheduled
      const elapsed = Date.now() - last;
      if (elapsed >= intervalMs) {
        fire();
      } else {
        timer = setTimeout(() => {
          timer = null;
          fire();
        }, intervalMs - elapsed);
      }
    },
    flush() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      fire();
    },
    dispose() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}

// ---------------------------------------------------------------------------
// Chat sessions (transcript content; the live stream is api/sse.ts).
// Product-scoped in v2 (api.md §10).
// ---------------------------------------------------------------------------

export function useSessions(pid: Ulid | undefined): UseQueryResult<ChatSessionList> {
  return useQuery({
    queryKey: queryKeys.sessions(pid ?? ''),
    queryFn: ({ signal }) => apiGet<ChatSessionList>(`/products/${pid}/chat/sessions`, signal),
    enabled: pid !== undefined && pid.length > 0,
  });
}

export function useSession(sessionId: Ulid | undefined): UseQueryResult<ChatSessionDetail> {
  return useQuery({
    queryKey: queryKeys.session(sessionId ?? ''),
    queryFn: ({ signal }) => apiGet<ChatSessionDetail>(`/chat/sessions/${sessionId}`, signal),
    enabled: sessionId !== undefined && sessionId.length > 0,
  });
}

export function useCreateSession(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChatSessionRequest) =>
      apiPost<ChatSessionCreated>(`/products/${pid}/chat/sessions`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions(pid) });
    },
  });
}

export function useDeleteSession(pid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: Ulid) => apiDelete(`/chat/sessions/${sessionId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions(pid) });
    },
  });
}

/** After a stream ends (message.done / error / drop), re-sync the transcript. */
export function invalidateSession(qc: QueryClient, sessionId: Ulid): Promise<unknown> {
  return qc.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
}

// ---------------------------------------------------------------------------
// Me / BYOK (per-user; api.md §12 — was /api/settings in v1)
// ---------------------------------------------------------------------------

/** What kind of server this is; it does not change while the tab is open. */
export function useHealth(): UseQueryResult<HealthResponse> {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: ({ signal }) => apiGet<HealthResponse>('/health', signal),
    staleTime: Infinity,
  });
}

export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => apiGet<MeResponse>('/me', signal),
  });
}

export function usePatchMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchMeRequest) => apiPatch<MeResponse>('/me', body),
    // The picture answers the click, not the round trip (2026-09-10, owner:
    // "changing effort level is slow"): the cached account takes the new
    // values at once, the server's answer replaces it, an error puts the old
    // one back.
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: queryKeys.me });
      const before = qc.getQueryData<MeResponse>(queryKeys.me);
      if (before !== undefined) qc.setQueryData(queryKeys.me, { ...before, ...body });
      return { before };
    },
    onError: (_err, _body, context) => {
      if (context?.before !== undefined) qc.setQueryData(queryKeys.me, context.before);
    },
    onSuccess: (me) => {
      qc.setQueryData(queryKeys.me, me);
    },
  });
}

export function usePutApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => apiPut<MeResponse>('/me/api_key', { key }),
    onSuccess: (me) => {
      qc.setQueryData(queryKeys.me, me);
    },
  });
}

/**
 * Native OS picker (local-first: the server runs on this machine and opens a
 * real Finder dialog; the response is only what the dialog chose). 501
 * `picker_unavailable` on non-mac/headless — callers fall back to the manual
 * path field.
 */
export function useLocalPick() {
  return useMutation({
    mutationFn: (body: LocalPickRequest) => apiPost<LocalPickResponse>('/local/pick', body),
  });
}

export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete('/me/api_key'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}
